/**
 * Regulatory Affairs / Product Lifecycle hooks (C-1). Backed by
 * /api/v1/regulatory/products (real Postgres, regulatory.products) — the
 * Master Product Register seeded from Spectropharma's real register.
 */
import {
  useMutation, useQuery, useQueryClient,
  type UseMutationResult, type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface Product {
  productId: string;
  productCode: string;
  brandName: string | null;
  molecule: string | null;
  category: string;            // PHARMA | NUTRA | MD
  itemDescription: string | null;
  strength: string | null;
  strengthUnit: string | null;
  packSize: string | null;
  registrationNumber: string | null;
  certificateIssueDate: string | null;
  renewalDueDate: string | null;
  registrationStatus: string;  // REGISTERED | UNDER_REGISTRATION
  businessPartner: string | null;
  manufacturingModel: string | null;
  raStatus: string | null;
  createdAt: string;
}
export interface ProductStats {
  registered: number; under_registration: number;
  pharma: number; nutra: number; md: number;
  renewals_due_12m: number; expired: number;
}
export interface ProductListResp { items: Product[]; total: number; stats: ProductStats }

export interface ProductFilters { status?: string; category?: string; q?: string }

export function useProductsQuery(filters: ProductFilters = {}): UseQueryResult<ProductListResp> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.category) qs.set('category', filters.category);
  if (filters.q) qs.set('q', filters.q);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<ProductListResp>({
    queryKey: ['regulatory', 'products', filters],
    queryFn: ({ signal }) => apiFetch<ProductListResp>(`/api/v1/regulatory/products${suffix}`, { signal }),
    staleTime: 60_000,
  });
}
export function useProductDetailQuery(id: string | undefined): UseQueryResult<Product> {
  return useQuery<Product>({
    queryKey: ['regulatory', 'products', 'detail', id],
    queryFn: ({ signal }) => apiFetch<Product>(`/api/v1/regulatory/products/${encodeURIComponent(id!)}`, { signal }),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export interface NewProductInput {
  itemDescription: string; brandName?: string; molecule?: string; category?: string;
  strength?: string; strengthUnit?: string; packSize?: string; registrationNumber?: string;
  certificateIssueDate?: string; registrationStatus?: string; businessPartner?: string;
}
export function useCreateProduct(): UseMutationResult<Product, Error, NewProductInput> {
  const qc = useQueryClient();
  return useMutation<Product, Error, NewProductInput>({
    mutationFn: (body) => apiFetch<Product>('/api/v1/regulatory/products', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['regulatory', 'products'] }),
  });
}

export const CATEGORY_LABEL: Record<string, string> = { PHARMA: 'Pharmaceutical', NUTRA: 'Nutraceutical', MD: 'Medical Device' };
export const REGSTATUS_LABEL: Record<string, string> = { REGISTERED: 'Registered', UNDER_REGISTRATION: 'Under registration' };

// ---- C-1b — Registration tracker -------------------------------------------
export interface RegistrationHistoryRow { event_kind: string; from_stage: string | null; to_stage: string | null; note: string | null; at_utc: string; actor_name: string | null }
export interface Registration {
  registrationId: string;
  registrationCode: string;
  productId: string | null;
  productName: string;
  market: string;
  authority: string;
  dosageForm: string | null;
  strength: string | null;
  category: string;
  stage: string;
  submissionDate: string | null;
  targetApprovalDate: string | null;
  approvalDate: string | null;
  ownerName: string | null;
  notes: string | null;
  history?: RegistrationHistoryRow[];
}
export interface RegistrationStats { in_progress: number; in_review: number; deficiency: number; approved: number; launched: number; overdue: number }
export interface RegistrationListResp { items: Registration[]; total: number; stats: RegistrationStats }

export function useRegistrationsQuery(filters: { stage?: string; category?: string } = {}): UseQueryResult<RegistrationListResp> {
  const qs = new URLSearchParams();
  if (filters.stage) qs.set('stage', filters.stage);
  if (filters.category) qs.set('category', filters.category);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<RegistrationListResp>({
    queryKey: ['regulatory', 'registrations', filters],
    queryFn: ({ signal }) => apiFetch<RegistrationListResp>(`/api/v1/regulatory/registrations${suffix}`, { signal }),
    staleTime: 30_000,
  });
}
export function useRegistrationDetailQuery(id: string | undefined): UseQueryResult<Registration> {
  return useQuery<Registration>({
    queryKey: ['regulatory', 'registrations', 'detail', id],
    queryFn: ({ signal }) => apiFetch<Registration>(`/api/v1/regulatory/registrations/${encodeURIComponent(id!)}`, { signal }),
    enabled: !!id,
    staleTime: 30_000,
  });
}
export interface NewRegistrationInput { productName: string; productId?: string; category?: string; market?: string; authority?: string; dosageForm?: string; strength?: string; targetApprovalDate?: string }
export function useCreateRegistration(): UseMutationResult<Registration, Error, NewRegistrationInput> {
  const qc = useQueryClient();
  return useMutation<Registration, Error, NewRegistrationInput>({
    mutationFn: (body) => apiFetch<Registration>('/api/v1/regulatory/registrations', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['regulatory', 'registrations'] }),
  });
}
export function useAdvanceRegistration(id: string | undefined): UseMutationResult<unknown, Error, { toStage: string; note?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { toStage: string; note?: string }) =>
      apiFetch(`/api/v1/regulatory/registrations/${encodeURIComponent(id!)}/advance`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['regulatory'] }),
  });
}

// The SFDA/GCC registration workflow (RA.docx).
export const REGISTRATION_STAGES = ['CONCEPT', 'DOSSIER_PREP', 'SUBMISSION', 'AUTHORITY_REVIEW', 'DEFICIENCY_RESPONSE', 'APPROVED', 'LAUNCHED'];
export const STAGE_LABEL: Record<string, string> = {
  CONCEPT: 'Concept', DOSSIER_PREP: 'Dossier prep', SUBMISSION: 'Submission', AUTHORITY_REVIEW: 'Authority review',
  DEFICIENCY_RESPONSE: 'Deficiency response', APPROVED: 'Approved', LAUNCHED: 'Launched', WITHDRAWN: 'Withdrawn',
};
export const REGISTRATION_FSM: Record<string, string[]> = {
  CONCEPT: ['DOSSIER_PREP', 'WITHDRAWN'],
  DOSSIER_PREP: ['SUBMISSION', 'WITHDRAWN'],
  SUBMISSION: ['AUTHORITY_REVIEW', 'WITHDRAWN'],
  AUTHORITY_REVIEW: ['DEFICIENCY_RESPONSE', 'APPROVED', 'WITHDRAWN'],
  DEFICIENCY_RESPONSE: ['AUTHORITY_REVIEW', 'APPROVED', 'WITHDRAWN'],
  APPROVED: ['LAUNCHED'],
  LAUNCHED: [],
  WITHDRAWN: [],
};

// ---- C-1c — Variation management -------------------------------------------
export interface VariationHistoryRow { event_kind: string; from_status: string | null; to_status: string | null; note: string | null; at_utc: string; actor_name: string | null }
export interface Variation {
  variationId: string;
  variationCode: string;
  productId: string | null;
  productName: string;
  variationType: string;
  description: string;
  changeRequestId: string | null;
  changeRequestTitle: string | null;
  market: string;
  authority: string;
  status: string;
  submissionDate: string | null;
  approvalDate: string | null;
  ownerName: string | null;
  history?: VariationHistoryRow[];
}
export interface VariationStats { in_progress: number; under_review: number; major_open: number; implemented: number }
export interface VariationListResp { items: Variation[]; total: number; stats: VariationStats }

export function useVariationsQuery(filters: { status?: string; type?: string } = {}): UseQueryResult<VariationListResp> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.type) qs.set('type', filters.type);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<VariationListResp>({
    queryKey: ['regulatory', 'variations', filters],
    queryFn: ({ signal }) => apiFetch<VariationListResp>(`/api/v1/regulatory/variations${suffix}`, { signal }),
    staleTime: 30_000,
  });
}
export function useVariationDetailQuery(id: string | undefined): UseQueryResult<Variation> {
  return useQuery<Variation>({
    queryKey: ['regulatory', 'variations', 'detail', id],
    queryFn: ({ signal }) => apiFetch<Variation>(`/api/v1/regulatory/variations/${encodeURIComponent(id!)}`, { signal }),
    enabled: !!id,
    staleTime: 30_000,
  });
}
export interface NewVariationInput { productName: string; description: string; variationType?: string; productId?: string; changeRequestId?: string; authority?: string }
export function useCreateVariation(): UseMutationResult<Variation, Error, NewVariationInput> {
  const qc = useQueryClient();
  return useMutation<Variation, Error, NewVariationInput>({
    mutationFn: (body) => apiFetch<Variation>('/api/v1/regulatory/variations', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['regulatory', 'variations'] }),
  });
}
export function useVariationTransition(id: string | undefined): UseMutationResult<unknown, Error, { toStatus: string; note?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { toStatus: string; note?: string }) =>
      apiFetch(`/api/v1/regulatory/variations/${encodeURIComponent(id!)}/transition`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['regulatory', 'variations'] }),
  });
}
export const VARIATION_FSM: Record<string, string[]> = {
  DRAFT: ['SUBMITTED', 'WITHDRAWN'],
  SUBMITTED: ['UNDER_REVIEW', 'WITHDRAWN'],
  UNDER_REVIEW: ['APPROVED', 'REJECTED'],
  APPROVED: ['IMPLEMENTED'],
  IMPLEMENTED: [],
  REJECTED: [],
  WITHDRAWN: [],
};
export const VARIATION_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft', SUBMITTED: 'Submitted', UNDER_REVIEW: 'Under review', APPROVED: 'Approved',
  IMPLEMENTED: 'Implemented', REJECTED: 'Rejected', WITHDRAWN: 'Withdrawn',
};
export const VARIATION_TYPE_LABEL: Record<string, string> = {
  TYPE_IA: 'Type IA (minor, notify)', TYPE_IB: 'Type IB (minor, tell-wait)', TYPE_II: 'Type II (major, prior approval)', NOTIFICATION: 'Notification',
};

// ---- C-1e — Renewals tracker -----------------------------------------------
// Registered products needing marketing-authorisation renewal (cert + 5y),
// bucketed so RA can work the deadline wall front-to-back.
export interface RenewalRow {
  productId: string; productCode: string; brandName: string | null; molecule: string | null;
  category: string; registrationNumber: string | null; certificateIssueDate: string | null;
  renewalDueDate: string | null; daysToDue: number | null; bucket: string;
}
export interface RenewalStats { overdue: number; due_90: number; due_180: number; due_365: number; future: number; total: number }
export interface RenewalListResp { items: RenewalRow[]; total: number; stats: RenewalStats }

export function useRenewalsQuery(bucket?: string): UseQueryResult<RenewalListResp> {
  const suffix = bucket ? `?bucket=${encodeURIComponent(bucket)}` : '';
  return useQuery<RenewalListResp>({
    queryKey: ['regulatory', 'renewals', bucket ?? 'all'],
    queryFn: ({ signal }) => apiFetch<RenewalListResp>(`/api/v1/regulatory/renewals${suffix}`, { signal }),
    staleTime: 60_000,
  });
}
export const RENEWAL_BUCKET_LABEL: Record<string, string> = {
  OVERDUE: 'Overdue', DUE_90: 'Due ≤ 90 days', DUE_180: 'Due ≤ 180 days', DUE_365: 'Due ≤ 12 months', FUTURE: 'Beyond 12 months',
};

// ---- C-1e — Country / market matrix ----------------------------------------
export interface MarketRow {
  market: string; authority: string; total: number;
  launched: number; approved: number; inProgress: number; withdrawn: number;
}
export interface MarketTotals { total: number; launched: number; approved: number; inProgress: number; withdrawn: number }
export interface MarketMatrixResp { markets: MarketRow[]; totals: MarketTotals; marketCount: number }

export function useMarketMatrixQuery(): UseQueryResult<MarketMatrixResp> {
  return useQuery<MarketMatrixResp>({
    queryKey: ['regulatory', 'market-matrix'],
    queryFn: ({ signal }) => apiFetch<MarketMatrixResp>('/api/v1/regulatory/market-matrix', { signal }),
    staleTime: 60_000,
  });
}

// ---- C-1f — Post-approval commitments --------------------------------------
export interface CommitmentHistoryRow { event_kind: string; from_status: string | null; to_status: string | null; note: string | null; at_utc: string; actor_name: string | null }
export interface Commitment {
  commitmentId: string;
  commitmentNumber: string;
  registrationId: string | null;
  registrationCode: string | null;
  productId: string | null;
  productName: string | null;
  title: string;
  description: string | null;
  commitmentType: string;
  authority: string;
  status: string;
  dueDate: string | null;
  submittedDate: string | null;
  fulfilledDate: string | null;
  ownerName: string | null;
  history?: CommitmentHistoryRow[];
}
export interface CommitmentStats { total: number; open: number; overdue: number; due_soon: number; fulfilled: number }
export interface CommitmentListResp { items: Commitment[]; total: number; stats: CommitmentStats }

export function useCommitmentsQuery(filters: { status?: string; type?: string } = {}): UseQueryResult<CommitmentListResp> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.type) qs.set('type', filters.type);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<CommitmentListResp>({
    queryKey: ['regulatory', 'commitments', filters],
    queryFn: ({ signal }) => apiFetch<CommitmentListResp>(`/api/v1/regulatory/commitments${suffix}`, { signal }),
    staleTime: 30_000,
  });
}
export function useCommitmentDetailQuery(id: string | undefined): UseQueryResult<Commitment> {
  return useQuery<Commitment>({
    queryKey: ['regulatory', 'commitments', 'detail', id],
    queryFn: ({ signal }) => apiFetch<Commitment>(`/api/v1/regulatory/commitments/${encodeURIComponent(id!)}`, { signal }),
    enabled: !!id,
    staleTime: 30_000,
  });
}
export interface NewCommitmentInput { title: string; description?: string; commitmentType?: string; authority?: string; dueDate?: string; registrationId?: string; productId?: string }
export function useCreateCommitment(): UseMutationResult<Commitment, Error, NewCommitmentInput> {
  const qc = useQueryClient();
  return useMutation<Commitment, Error, NewCommitmentInput>({
    mutationFn: (body) => apiFetch<Commitment>('/api/v1/regulatory/commitments', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['regulatory', 'commitments'] }),
  });
}
export function useCommitmentTransition(id: string | undefined): UseMutationResult<unknown, Error, { toStatus: string; note?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { toStatus: string; note?: string }) =>
      apiFetch(`/api/v1/regulatory/commitments/${encodeURIComponent(id!)}/transition`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['regulatory', 'commitments'] }),
  });
}
export const COMMITMENT_FSM: Record<string, string[]> = {
  OPEN: ['IN_PROGRESS', 'CANCELLED'],
  IN_PROGRESS: ['SUBMITTED', 'CANCELLED'],
  SUBMITTED: ['FULFILLED', 'IN_PROGRESS'],
  FULFILLED: [],
  CANCELLED: [],
};
export const COMMITMENT_STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open', IN_PROGRESS: 'In progress', SUBMITTED: 'Submitted', FULFILLED: 'Fulfilled', CANCELLED: 'Cancelled',
};
export const COMMITMENT_TYPE_LABEL: Record<string, string> = {
  STABILITY_DATA: 'Stability data', PMS: 'Post-market surveillance', LABELING_UPDATE: 'Labelling update',
  MANUFACTURING_CHANGE: 'Manufacturing change', PV_STUDY: 'PV study', OTHER: 'Other',
};
